import { useState } from "react";

import featureTogglesNames from "@src/assets/feature_toggles.json";
import SettingsIcon from "@src/assets/icons/settings.svg";
import { useAuthentication } from "@src/auth/useAuthentication";
import { FeatureToggleSettingsModal } from "@src/components/app_layout/components/feature_toggle_settings_modal";
import { HelpInfo } from "@src/components/app_layout/components/help_info/index";
import { AuthorizedContent } from "@src/components/authorized_content";
import * as constants from "@src/constants";
import { ROLES } from "@src/constants";
import { NotificationPopover } from "@src/features/notification/components/notification_popover";
import useFeatureToggle from "@src/hooks/use_feature_toggle";
import { Button, Layout, Tooltip } from "antd";

import { Outlet } from "react-router-dom";
import "./app_layout.less";
import { ImpersionateUser } from "./components/impersionate_user";
import { Logo } from "./components/logo";
import { Navigation } from "./components/navigation";
import { UserArea } from "./components/user_area";

const { Content } = Layout;

const AppLayout = () => {
  const [activeRoute, setActiveRoute] = useState(null);
  const [
    featureToggleSettingsModalVisible,
    setFeatureToggleSettingsModalVisible,
  ] = useState(false);
  const { hasAccess, isAuthenticating, reasonPhrase, loggedInUser } =
    useAuthentication();
  const plResourcePlanAuditEnabled: boolean = useFeatureToggle(
    "plResourcePlanAudit"
  );

  if (isAuthenticating) return <h1>Loading...</h1>;

  if (!hasAccess) {
    return (
      <p data-testid="unauthorized-message">
        You don't have access privilege. Please contact support team or send a
        mail to <b>{constants.SUPPORT_ID}</b> with the reason below
        <div>
          <b>Reason:</b> ({reasonPhrase.toString().replace(",", " | ")})
        </div>
      </p>
    );
  }

  return (
    <>
      <FeatureToggleSettingsModal
        modalVisible={featureToggleSettingsModalVisible}
        setModalVisible={setFeatureToggleSettingsModalVisible}
      />
      <Layout className="layout">
        <AuthorizedContent
          roles={[
            ROLES.PROJECT_LEAD,
            ROLES.ADMIN,
            ROLES.TEAM_LEAD,
            ROLES.DEPUTY,
            ROLES.MANAGER,
          ]}
        >
          <div className="main-header">
            <div className="header-navigation">
              <Logo />
              <Navigation
                loggedInUser={loggedInUser}
                setActiveRoute={setActiveRoute}
                activeRoute={activeRoute}
              />
              <div className="help-icon-user-section">
                {plResourcePlanAuditEnabled && <NotificationPopover />}
                <HelpInfo />
                <UserArea loggedInUser={loggedInUser} />
              </div>
            </div>
          </div>
          <div className="sub-navigation">
            {loggedInUser.applicationRoles.includes(ROLES.ADMIN) &&
              featureTogglesNames.length > 0 && (
                <Tooltip
                  mouseEnterDelay={0.5}
                  placement="top"
                  title={"Feature Toggle Settings"}
                >
                  <Button
                    type="link"
                    onClick={() => setFeatureToggleSettingsModalVisible(true)}
                    icon={<SettingsIcon />}
                    data-testid="feature-toggle-button"
                  />
                </Tooltip>
              )}
            <ImpersionateUser setActiveRoute={setActiveRoute} />
          </div>
        </AuthorizedContent>
        <Content>
          <div className="site-layout-content">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </>
  );
};

export default AppLayout;
